import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { MONO_FONT } from "../../components/metrics/metricsUi";
import { impactText } from "../../utils/commandCenterPageDefaults";
import { eyebrowSx } from "../purchase-order-analysis/parts";
import { SECTION_ICONS, sectionTone } from "./sectionStyle";

const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/**
 * One section summarised: the number that matters, what it means and the
 * biggest item. The whole tile is a button that scrolls to its section.
 */
const SummaryTile = ({ ui, tile, onClick }) => {
  const { colors } = ui;
  const Icon = SECTION_ICONS[tile.key];
  const { fg, bg } = sectionTone(ui, tile.summary);
  const { summary, top } = tile;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={`${tile.label}: ${tile.value} ${tile.unit}`}
      sx={{
        ...ui.card,
        borderTop: `3px solid ${fg}`,
        p: "16px 18px 14px",
        width: "100%",
        textAlign: "left",
        fontFamily: "inherit",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 120ms ease, transform 120ms ease",
        "&:hover": { boxShadow: `0 4px 14px ${ui.darkMode ? "rgba(0,0,0,0.35)" : "rgba(16,24,40,0.10)"}`, transform: "translateY(-1px)" },
        "&:focus-visible": { outline: `2px solid ${ui.accent}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "8px", display: "grid", placeItems: "center", backgroundColor: bg }}>
          <Icon sx={{ fontSize: 16, color: fg }} />
        </Box>
        <Typography sx={{ ...eyebrowSx(colors), flex: 1 }}>{tile.label}</Typography>
        <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 0.9, py: 0.2, borderRadius: "10px", color: fg, backgroundColor: bg }}>
          {summary.today ? "Act today" : summary.count ? "This week" : "All clear"}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 1.5 }}>
        <Typography
          sx={{ fontFamily: MONO_FONT, fontSize: 30, fontWeight: 700, lineHeight: 1, color: tile.urgent ? ui.danger : colors.textPrimary, fontVariantNumeric: "tabular-nums" }}
        >
          {tile.value}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>{tile.unit}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mt: 0.6 }}>{tile.caption}</Typography>

      <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${colors.border}`, display: "flex", alignItems: "center", gap: 1, minHeight: 22 }}>
        {top ? (
          <>
            <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, flexShrink: 0 }}>Biggest</Typography>
            <Typography sx={{ ...ellipsis, flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: colors.textPrimary }}>
              {top.title}
            </Typography>
            <Typography sx={{ fontFamily: MONO_FONT, fontSize: 12, color: colors.textSecondary, flexShrink: 0 }}>{impactText(top)}</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 12, color: colors.textTertiary, flex: 1 }}>Nothing to act on</Typography>
        )}
        <ArrowDownwardIcon sx={{ fontSize: 15, color: ui.accent, flexShrink: 0 }} />
      </Box>
    </Box>
  );
};

export default SummaryTile;
